/**
 * CardPaymentForm — Formulário público de pagamento com cartão de crédito.
 *
 * Usado dentro de /c/:slug (CheckoutPublic). Coleta dados do cartão e
 * do titular, executa validações client-side (Luhn, expiry, CVV, CPF),
 * envia para a edge `public-checkout` (action 'charge_card') e faz
 * polling em `get_status` quando a cobrança fica em processing/unknown
 * (cenário 3DS / análise antifraude).
 *
 * Segurança:
 *   - PAN/CVV NUNCA são persistidos no estado global ou no console.
 *   - O log local usa apenas brand + last4.
 *   - Idempotency key é regenerada por sessão (não reaproveitada entre cards).
 *
 * RELEASE: 2026-06-20 v1.0.0 — Sub-fase A 4.2-4.4 (cartão + 3DS via polling).
 * RELEASE: 2026-06-20 v1.1.0 — Sub-fase C: challenge 3DS via popup/iframe
 *   (`authentication_url` retornada pela edge); detecta popup bloqueado e
 *   oferece fallback de iframe in-page + link "abrir em nova guia"; polling
 *   continua em paralelo até cobrança transitar para 'pago' ou tempo esgotar.
 * RELEASE: 2026-06-20 v1.2.0 — Lê envelope padronizado da edge `public-checkout`
 *   ({ success, error_code, user_message, support_id }) e exibe via
 *   <CheckoutErrorBanner>. Nunca mais mostra a string da SDK "Edge Function
 *   returned a non-2xx status code" — mensagens são comerciais em PT-BR.
 * RELEASE: 2026-06-21 v1.3.0 — Coleta CEP e Número do endereço do titular
 *   (exigidos pelo Asaas em `creditCardHolderInfo.postalCode` / `addressNumber`).
 *   Sem esses campos, a tokenização sempre falha com HTTP 422
 *   "Informe o CEP do titular do cartão." (causa raiz do att_36235a18).
 * RELEASE: 2026-06-21 v1.3.1 — Campo Telefone migrado para <PhoneInput>
 *   (máscara + DDI), reaproveitando a mesma estrutura do Agendamento Online
 *   (PublicSchedulingForm). Antes era <Input> cru aceitando qualquer texto.
 */

import { useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ShieldCheck, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { CheckoutErrorBanner } from './components/CheckoutErrorBanner';
import { AcceptedCardBrands } from './components/AcceptedCardBrands';
import { CreditCardVisual } from './components/CreditCardVisual';

import { getFallbackMessage, getNetworkErrorMessage } from '@/lib/checkout/userMessages';
import { InlineNotice } from '@/components/ui/inline-notice';
import { PhoneInput } from '@/components/ui/phone-input';
import { extractCountryCode } from '@/lib/country-codes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


const logger = createLogger('CardPaymentForm');

// =========================================================================
// Tipos & helpers
// =========================================================================

export interface CardPaymentPlan {
  /** Política de pagamento de parcelamento por cartão (tenant setting). */
  mode: 'pay_all_remaining' | 'pay_single_only';
  /** Quantidade de parcelas em aberto (atual + futuras). */
  total_open_installments: number;
  /** Soma do valor de TODAS as parcelas em aberto. */
  total_amount: number;
  /** Número da parcela atual (a que está sendo paga agora). */
  current_installment_number: number | null;
  /** Valor da parcela atual. */
  current_installment_amount: number | null;
}

export interface CardPaymentFormProps {
  slug: string;
  /** Dispara quando o pagamento é confirmado (status final 'pago'). */
  onPaid?: () => void;
  /** Card visual à esquerda (para preencher em tempo real). */
  onCardChange?: (preview: CardPreview) => void;
  /**
   * Slot opcional renderizado logo abaixo do cabeçalho ("Cartão de crédito"),
   * antes da seção "Dados do cartão". Usado para exibir o aviso de cobrança
   * recorrente dentro do bloco do método.
   */
  recurringNotice?: React.ReactNode;
  /**
   * v1.4.0 — Plano de pagamento por cartão (cobranças parceladas).
   * Quando `mode === 'pay_all_remaining'` e `total_open_installments > 1`,
   * o submit exibe um modal de confirmação detalhando o valor total e a
   * quantidade total de parcelas que serão cobradas no cartão.
   */
  cardPaymentPlan?: CardPaymentPlan | null;
}

export interface CardPreview {
  number: string;
  holder: string;
  expiry: string;
  brand: CardBrand;
  focus: 'number' | 'holder' | 'expiry' | 'cvv' | null;
}

export type CardBrand = 'visa' | 'master' | 'amex' | 'elo' | 'hipercard' | 'unknown';

/** Detecta bandeira pelos primeiros dígitos. */
function detectBrand(panDigits: string): CardBrand {
  if (/^4/.test(panDigits)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(panDigits)) return 'master';
  if (/^3[47]/.test(panDigits)) return 'amex';
  if (/^(636368|438935|504175|451416|509|627780|636297|65003[1-3]|6500(3[5-9]|4[0-9]|5[0-1])|6504(0[5-9]|1[0-9]|2[0-9]|3[0-9])|650(48[5-9]|49[0-9]|50[0-9]|51[0-9]|52[0-9]|53[0-9]|54[0-9]|55[0-8])|6505(8[5-9]|9[0-9])|6506(0[0-9]|1[0-9]|2[0-9]|3[0-9]|4[0-9]|5[0-9]|6[0-9]|7[0-8])|6507(0[0-9]|1[0-8])|6507(2[0-7])|650(9(0[1-9]|1[0-9]|2[0-9]))|6516(5[2-9]|6[0-9]|7[0-9])|6550(0[0-9]|1[1-9])|6550(2[1-9]|3[0-9]|4[0-9]|5[0-8]))/.test(panDigits)) return 'elo';
  if (/^(606282|3841)/.test(panDigits)) return 'hipercard';
  return 'unknown';
}

/** Algoritmo de Luhn para validar PAN. */
function luhnValid(panDigits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = panDigits.length - 1; i >= 0; i--) {
    let n = parseInt(panDigits.charAt(i), 10);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0 && panDigits.length >= 13;
}

/** Formata PAN agrupando em blocos de 4 (Amex: 4-6-5). */
function formatPan(value: string, brand: CardBrand): string {
  const d = value.replace(/\D/g, '').slice(0, 19);
  if (brand === 'amex') {
    return d.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' '),
    );
  }
  return d.replace(/(.{4})/g, '$1 ').trim();
}

/** Formata MM/AA. */
function formatExpiry(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 4);
  if (d.length < 3) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}

/** Valida CPF (algoritmo padrão). */
function cpfValid(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  let s1 = 0;
  let s2 = 0;
  for (let i = 0; i < 9; i++) {
    s1 += parseInt(d[i]) * (10 - i);
    s2 += parseInt(d[i]) * (11 - i);
  }
  const v1 = ((s1 * 10) % 11) % 10;
  s2 += v1 * 2;
  const v2 = ((s2 * 10) % 11) % 10;
  return v1 === parseInt(d[9]) && v2 === parseInt(d[10]);
}

/** Formata CPF como 000.000.000-00. */
function formatCpf(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/** Valida CNPJ (algoritmo padrão). */
function cnpjValid(cnpj: string): boolean {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false;
  const calc = (base: string, weights: number[]) => {
    const sum = weights.reduce((acc, w, i) => acc + parseInt(base[i]) * w, 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const v1 = calc(d.slice(0, 12), w1);
  const v2 = calc(d.slice(0, 12) + v1, w2);
  return v1 === parseInt(d[12]) && v2 === parseInt(d[13]);
}

/** Valida CPF (11 dígitos) OU CNPJ (14 dígitos). */
function docValid(value: string): boolean {
  const d = value.replace(/\D/g, '');
  if (d.length === 11) return cpfValid(d);
  if (d.length === 14) return cnpjValid(d);
  return false;
}

/** Formata dinamicamente como CPF (≤11 dígitos) ou CNPJ (≥12 dígitos). */
function formatCpfCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // CNPJ: 00.000.000/0000-00
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/** Formata CEP brasileiro como 00000-000 (até 8 dígitos). */
function formatCep(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** Gera idempotency key segura (32 chars hex). */
function newIdempotencyKey(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// =========================================================================
// Componente
// =========================================================================
type FormState = 'idle' | 'submitting' | 'polling' | 'challenge' | 'paid' | 'failed';

export function CardPaymentForm({ slug, onPaid, onCardChange, recurringNotice, cardPaymentPlan }: CardPaymentFormProps) {
  const [number, setNumber] = useState('');
  const [holder, setHolder] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // v1.3.0 — Asaas exige CEP e número do endereço no holderInfo da tokenização.
  const [cep, setCep] = useState('');
  const [addressNumber, setAddressNumber] = useState('');

  const [focus, setFocus] = useState<CardPreview['focus']>(null);
  const [state, setState] = useState<FormState>('idle');
  // v1.2.0 — estado de erro estruturado para banner premium com support_id
  const [errorState, setErrorState] = useState<
    { message: string; code?: string | null; supportId?: string | null } | null
  >(null);
  // Mantemos `setErrorMsg` como helper local para reduzir o diff dos sites de chamada
  const setErrorMsg = (message: string) =>
    setErrorState({ message, code: null, supportId: null });

  // v1.4.0 — Modal de confirmação para cobranças parceladas com política
  // 'pay_all_remaining' (Asaas processa todas as parcelas em aberto em uma
  // única transação no cartão). Quando aberto, o submit aguarda confirmação
  // explícita do cliente antes de chamar `charge_card`.
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);

  // Estado do challenge 3DS — quando presente, mostramos iframe + link externo
  const [challenge, setChallenge] = useState<{
    url: string;
    popupBlocked: boolean;
    popupRef: Window | null;
  } | null>(null);

  const panDigits = number.replace(/\D/g, '');
  const brand = useMemo(() => detectBrand(panDigits), [panDigits]);

  // Propaga preview do cartão para a coluna visual
  const pushPreview = (
    next: Partial<{ number: string; holder: string; expiry: string; focus: CardPreview['focus'] }>,
  ) => {
    onCardChange?.({
      number: next.number ?? number,
      holder: next.holder ?? holder,
      expiry: next.expiry ?? expiry,
      brand: detectBrand((next.number ?? number).replace(/\D/g, '')),
      focus: next.focus ?? focus,
    });
  };

  // -----------------------------------------------------------------------
  // Validação completa do formulário
  // -----------------------------------------------------------------------
  const validate = (): string | null => {
    if (!luhnValid(panDigits)) return 'Número de cartão inválido.';
    if (holder.trim().length < 3) return 'Informe o nome impresso no cartão.';
    const [mm, yy] = expiry.split('/');
    const m = parseInt(mm ?? '', 10);
    const y = 2000 + parseInt(yy ?? '', 10);
    if (!m || m < 1 || m > 12 || !y) return 'Validade inválida.';
    const now = new Date();
    const expDate = new Date(y, m - 1, 1);
    expDate.setMonth(expDate.getMonth() + 1);
    if (expDate <= now) return 'Cartão vencido.';
    const cvvLen = brand === 'amex' ? 4 : 3;
    if (cvv.length !== cvvLen) return `CVV deve ter ${cvvLen} dígitos.`;
    if (!docValid(cpf)) return 'CPF ou CNPJ inválido.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'E-mail inválido.';
    // v1.3.1 — PhoneInput devolve valor com DDI (ex.: +5511999999999). Extraímos
    // somente os dígitos nacionais para validar (mínimo 10 = fixo, 11 = celular).
    if (extractCountryCode(phone).localNumber.replace(/\D/g, '').length < 10) return 'Telefone inválido.';
    // v1.3.0 — CEP e número são obrigatórios para a tokenização Asaas
    if (cep.replace(/\D/g, '').length !== 8) return 'CEP do titular inválido. Informe os 8 dígitos.';
    if (addressNumber.trim().length < 1) return 'Informe o número do endereço do titular.';
    return null;
  };

  // -----------------------------------------------------------------------
  // Polling de status (fallback para 3DS / processing)
  // -----------------------------------------------------------------------
  async function pollStatus(maxMs = 90_000): Promise<'pago' | 'failed' | 'timeout'> {
    const start = Date.now();
    let delay = 2500;
    while (Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, delay));
      try {
        const { data, error } = await supabase.functions.invoke('public-checkout', {
          body: { action: 'get_status', slug },
        });
        if (error) {
          logger.warn('Polling falhou', { err: error.message });
        } else {
          const status = (data as { status?: string })?.status;
          if (status === 'pago') return 'pago';
          if (status === 'cancelado' || status === 'estornado') return 'failed';
        }
      } catch (err) {
        logger.warn('Polling exception', { err: String(err) });
      }
      delay = Math.min(delay * 1.3, 6000);
    }
    return 'timeout';
  }

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------

  /** v1.4.0 — true quando o tenant exige confirmar pagamento de TODAS as
   *  parcelas em aberto no cartão (cobrança parcelada). */
  const requiresMultiInstallmentConfirm =
    !!cardPaymentPlan &&
    cardPaymentPlan.mode === 'pay_all_remaining' &&
    cardPaymentPlan.total_open_installments > 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorState(null);
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    // v1.4.0 — Intercepta para exigir confirmação explícita do cliente
    // antes de submeter a cobrança no cartão (modal premium).
    if (requiresMultiInstallmentConfirm) {
      setConfirmAllOpen(true);
      return;
    }
    await runCharge();
  };

  /** Executa de fato a chamada `charge_card` na edge. Extraído de
   *  handleSubmit para reuso pelo modal de confirmação multi-parcelas. */
  const runCharge = async () => {
    setErrorState(null);
    setState('submitting');

    const [mm, yy] = expiry.split('/');
    const idempotency_key = newIdempotencyKey();


    try {
      const { data, error } = await supabase.functions.invoke('public-checkout', {
        body: {
          action: 'charge_card',
          slug,
          idempotency_key,
          card: {
            holder_name: holder.trim(),
            number: panDigits,
            expiry_month: parseInt(mm, 10),
            expiry_year: 2000 + parseInt(yy, 10),
            cvv,
          },
          holder_info: {
            name: holder.trim(),
            email: email.trim(),
            cpf_cnpj: cpf.replace(/\D/g, ''),
            phone: extractCountryCode(phone).localNumber.replace(/\D/g, ''),
            // v1.3.0 — exigidos pelo Asaas em creditCardHolderInfo
            postal_code: cep.replace(/\D/g, ''),
            address_number: addressNumber.trim().slice(0, 20),
          },
        },
      });

      // v1.2.0 — A edge `public-checkout` agora retorna SEMPRE HTTP 200 com
      // envelope padronizado. `error` da SDK só aparece em falha real de
      // rede / 5xx do runtime. Tratamos com mensagem comercial.
      if (error) {
        logger.warn('Falha de rede no charge_card', { err: error.message });
        setErrorState({ message: getNetworkErrorMessage(), code: 'NETWORK_ERROR' });
        setState('failed');
        return;
      }

      const payload = (data ?? {}) as {
        success?: boolean;
        status?: string;
        requires_polling?: boolean;
        authentication_url?: string | null;
        // Envelope v1.5.0 (público-checkout)
        error_code?: string;
        error_category?: string;
        user_message?: string;
        support_id?: string;
      };

      // Sucesso ou estados de polling/3DS: cai nos branches abaixo.
      // Erro de negócio: envelope traz user_message + support_id.
      if (payload?.success === false || payload?.error_code) {
        setErrorState({
          message: payload.user_message || getFallbackMessage(payload.error_code),
          code: payload.error_code ?? null,
          supportId: payload.support_id ?? null,
        });
        setState('failed');
        return;
      }

      logger.info('Charge response', {
        success: payload?.success,
        status: payload?.status,
        last4: panDigits.slice(-4),
        brand,
        has3ds: !!payload?.authentication_url,
        support_id: payload?.support_id,
      });

      if (payload?.status === 'paid') {
        setState('paid');
        onPaid?.();
        return;
      }

      // ------------------------------------------------------------------
      // 3DS challenge: abre popup (ou cai em iframe inline se bloqueado) e
      // mantém polling em paralelo até a cobrança virar 'pago'.
      // ------------------------------------------------------------------
      if (payload?.authentication_url) {
        const url = payload.authentication_url;
        // Tenta abrir popup centralizado (não bloqueia por user gesture — está
        // dentro do submit). Algumas configurações ainda bloqueiam → fallback.
        const w = 480;
        const h = 640;
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;
        const popupRef = window.open(
          url,
          'asaas-3ds-challenge',
          `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );
        const popupBlocked = !popupRef || popupRef.closed || typeof popupRef.closed === 'undefined';
        setChallenge({ url, popupBlocked, popupRef });
        setState('challenge');

        // Polling estendido (até 5 min) — challenge 3DS pode levar a digitação
        // do OTP pelo usuário; mantemos a UI travada nesse estado.
        const result = await pollStatus(5 * 60_000);
        try { popupRef?.close(); } catch { /* ignore */ }
        setChallenge(null);

        if (result === 'pago') {
          setState('paid');
          onPaid?.();
        } else if (result === 'timeout') {
          setErrorMsg(
            'Não recebemos a confirmação da autenticação a tempo. Verifique seu app/banco e tente novamente.',
          );
          setState('failed');
        } else {
          setErrorMsg('Autenticação 3DS não concluída. Tente novamente.');
          setState('failed');
        }
        return;
      }

      if (payload?.requires_polling || payload?.status === 'processing' || payload?.status === 'unknown') {
        setState('polling');
        const result = await pollStatus();
        if (result === 'pago') {
          setState('paid');
          onPaid?.();
        } else if (result === 'timeout') {
          setErrorMsg(
            'Ainda estamos confirmando seu pagamento com a operadora. Você receberá uma notificação assim que for aprovado.',
          );
          setState('failed');
        } else {
          setErrorMsg('Pagamento não foi concluído.');
          setState('failed');
        }
        return;
      }

      setErrorMsg('Cartão recusado. Verifique os dados ou tente outro cartão.');
      setState('failed');
    } catch (err) {
      logger.error('Submit exception', { err: String(err) });
      setErrorMsg('Erro de conexão. Tente novamente em instantes.');
      setState('failed');
    }
  };

  // -----------------------------------------------------------------------
  // Renderização — estados de sucesso/erro substituem o formulário
  // -----------------------------------------------------------------------
  if (state === 'paid') {
    return (
      <div className="text-center py-10 space-y-3">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h2 className="text-xl font-semibold">Pagamento aprovado</h2>
        <p className="text-sm text-muted-foreground">
          Você receberá a confirmação por e-mail em instantes.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Estado de challenge 3DS: popup aberto OU fallback iframe inline.
  // O polling continua rodando em background até virar 'pago' ou timeout.
  // ---------------------------------------------------------------------
  if (state === 'challenge' && challenge) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Confirme com seu banco</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          O emissor do seu cartão exige uma autenticação adicional (3D Secure).
          {challenge.popupBlocked
            ? ' Concluído? Aguarde — vamos detectar automaticamente.'
            : ' Conclua na janela que abrimos para você.'}
        </p>

        {challenge.popupBlocked ? (
          // Popup bloqueado → renderiza iframe inline como fallback
          <div className="rounded-lg overflow-hidden border bg-background">
            <iframe
              src={challenge.url}
              title="Autenticação 3D Secure"
              className="w-full h-[520px] border-0"
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
            />
          </div>
        ) : (
          <div className="rounded-lg border bg-muted/30 p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
            <p className="text-sm">
              Aguardando você concluir a autenticação na janela aberta…
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <a
            href={challenge.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Abrir em nova guia
          </a>
          <span className="text-xs text-muted-foreground">
            Não feche esta página até a confirmação
          </span>
        </div>
      </div>
    );
  }

  const submitting = state === 'submitting' || state === 'polling';

  return (
    <form onSubmit={handleSubmit}>
      {/* Layout 2 colunas: form à esquerda + cartão visual sticky à direita.
          Em mobile usa flex-col-reverse para o cartão aparecer ACIMA do form. */}
      <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-12 items-start">
        {/* ============================ COLUNA ESQUERDA — FORM ============================ */}
        <div className="flex-1 w-full min-w-0 space-y-6">
          {/* Cabeçalho — apenas texto, sem cartão sobreposto */}
          <header>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Aprovação imediata
            </p>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-700" /> Cartão de crédito
            </h3>
            {/* Aviso de cobrança recorrente — abaixo do título */}
            {recurringNotice ? <div className="mt-3">{recurringNotice}</div> : null}
          </header>

          {/* Dados do cartão */}
          <section className="space-y-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Dados do cartão
            </p>

            <div className="space-y-2">
              <Label htmlFor="cc-number">Número do cartão</Label>
              <Input
                id="cc-number"
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder="0000 0000 0000 0000"
                value={formatPan(number, brand)}
                onChange={(e) => {
                  setNumber(e.target.value);
                  pushPreview({ number: e.target.value });
                }}
                onFocus={() => { setFocus('number'); pushPreview({ focus: 'number' }); }}
                onBlur={() => { setFocus(null); pushPreview({ focus: null }); }}
                maxLength={brand === 'amex' ? 17 : 23}
                disabled={submitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cc-name">Nome impresso no cartão</Label>
              <Input
                id="cc-name"
                autoComplete="cc-name"
                placeholder="Como aparece no cartão"
                value={holder}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().slice(0, 60);
                  setHolder(v);
                  pushPreview({ holder: v });
                }}
                onFocus={() => { setFocus('holder'); pushPreview({ focus: 'holder' }); }}
                onBlur={() => { setFocus(null); pushPreview({ focus: null }); }}
                disabled={submitting}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cc-exp">Validade</Label>
                <Input
                  id="cc-exp"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  placeholder="MM/AA"
                  value={expiry}
                  onChange={(e) => {
                    const v = formatExpiry(e.target.value);
                    setExpiry(v);
                    pushPreview({ expiry: v });
                  }}
                  onFocus={() => { setFocus('expiry'); pushPreview({ focus: 'expiry' }); }}
                  onBlur={() => { setFocus(null); pushPreview({ focus: null }); }}
                  maxLength={5}
                  disabled={submitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cc-cvv">CVV</Label>
                <Input
                  id="cc-cvv"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder={brand === 'amex' ? '4 dígitos' : '3 dígitos'}
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, brand === 'amex' ? 4 : 3))}
                  onFocus={() => { setFocus('cvv'); pushPreview({ focus: 'cvv' }); }}
                  onBlur={() => { setFocus(null); pushPreview({ focus: null }); }}
                  maxLength={4}
                  disabled={submitting}
                  required
                />
              </div>
            </div>
          </section>

          {/* Titular */}
          <section className="space-y-4 pt-2 border-t border-slate-100">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Dados do titular
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cc-cpf">CPF ou CNPJ</Label>
                <Input
                  id="cc-cpf"
                  inputMode="numeric"
                  placeholder="CPF ou CNPJ do titular"
                  value={formatCpfCnpj(cpf)}
                  onChange={(e) => setCpf(e.target.value)}
                  maxLength={18}
                  disabled={submitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cc-phone">Telefone</Label>
                <PhoneInput
                  id="cc-phone"
                  value={phone}
                  onChange={setPhone}
                  disabled={submitting}
                  compactSelector
                  aria-invalid={!!errorState}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cc-cep">CEP</Label>
                <Input
                  id="cc-cep"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="00000-000"
                  value={formatCep(cep)}
                  onChange={(e) => setCep(e.target.value)}
                  maxLength={9}
                  disabled={submitting}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cc-addr-number">Número do endereço</Label>
                <Input
                  id="cc-addr-number"
                  inputMode="text"
                  autoComplete="address-line2"
                  placeholder="Ex: 123 ou S/N"
                  value={addressNumber}
                  onChange={(e) => setAddressNumber(e.target.value.slice(0, 20))}
                  maxLength={20}
                  disabled={submitting}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc-email">E-mail para recibo</Label>
              <Input
                id="cc-email"
                type="email"
                autoComplete="email"
                placeholder="voce@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={120}
                disabled={submitting}
                required
              />
            </div>
          </section>

          {errorState && (
            <CheckoutErrorBanner
              message={errorState.message}
              code={errorState.code ?? undefined}
              supportId={errorState.supportId ?? undefined}
            />
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full h-12 text-base font-semibold bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-foreground)] shadow-md"
            disabled={submitting}
          >
            {state === 'submitting' && (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processando…</>
            )}
            {state === 'polling' && (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirmando com a operadora…</>
            )}
            {(state === 'idle' || state === 'failed') && (
              <><Lock className="w-4 h-4 mr-2" /> Pagar agora com segurança</>
            )}
          </Button>

          <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <Lock className="w-3 h-3" />
            <span>Dados criptografados ponta a ponta. Nunca armazenamos seu CVV.</span>
          </div>

          {/* Bandeiras de cartão aceitas — SVG inline, sem requisições HTTP */}
          <AcceptedCardBrands />
        </div>

        {/* ============================ COLUNA DIREITA — CARTÃO VISUAL ============================ */}
        <aside className="lg:w-[360px] w-full flex flex-col items-center shrink-0">
          <div className="lg:sticky lg:top-12 flex flex-col items-center">
            <CreditCardVisual
              preview={{ number, holder, expiry, brand, focus }}
            />
            <p className="mt-6 text-xs font-medium text-gray-400">
              Pré-visualização em tempo real
            </p>
          </div>
        </aside>
      </div>

      {/* ================================================================
          v1.4.0 — Modal de confirmação de pagamento multi-parcelas no cartão
          Exibido APENAS quando a cobrança é parcelada e o tenant configurou
          card_installment_payment_mode = 'pay_all_remaining'. Garante que o
          cliente entenda que ao pagar a parcela atual no cartão, TODAS as
          parcelas em aberto serão quitadas na mesma transação.
         ================================================================ */}
      {cardPaymentPlan && requiresMultiInstallmentConfirm && (
        <AlertDialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
          {/* v1.4.1 — Layout "Aviso destacado" (premium fintech).
              Mantém 100% da lógica original (runCharge / setConfirmAllOpen);
              apenas refina a apresentação visual do conteúdo do dialog. */}
          <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border/60 shadow-2xl">
            <div className="p-8">
              {/* Título — mantém AlertDialogTitle para acessibilidade (a11y) */}
              <AlertDialogTitle className="text-[22px] font-bold tracking-tight text-foreground">
                Confirmar pagamento no cartão
              </AlertDialogTitle>

              {/* Bloco de contexto destacado — padrão InlineNotice (barra lateral colorida) */}
              <AlertDialogDescription asChild>
                <div className="mt-4">
                  <InlineNotice variant="warning">
                    Você está pagando a{' '}
                    <span className="font-semibold text-foreground">
                      parcela {cardPaymentPlan.current_installment_number ?? '—'}
                    </span>
                    , mas o pagamento via cartão de crédito quita{' '}
                    <span className="font-semibold text-foreground">
                      todas as parcelas em aberto
                    </span>{' '}
                    em uma única transação.
                  </InlineNotice>
                </div>
              </AlertDialogDescription>

              {/* Card de detalhes — breakdown enxuto */}
              <div className="mt-6 space-y-4 rounded-2xl border border-border/60 bg-muted/40 p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">
                    Parcelas a serem pagas
                  </span>
                  <span className="rounded-full border border-border bg-background px-3 py-1 text-sm font-semibold text-foreground shadow-sm">
                    {cardPaymentPlan.total_open_installments}x no cartão
                  </span>
                </div>

                <div className="h-px bg-border/60" />

                <div className="flex items-end justify-between">
                  <span className="mb-1 text-sm font-medium text-muted-foreground">
                    Valor total cobrado
                  </span>
                  <span className="text-2xl font-bold tracking-tight text-foreground">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(cardPaymentPlan.total_amount)}
                  </span>
                </div>
              </div>

              {/* Disclaimer — letras pequenas, centradas */}
              <p className="mt-6 px-4 text-center text-[11px] leading-relaxed text-muted-foreground">
                O valor total será parcelado no seu cartão pelo emissor conforme
                as condições e taxas vigentes do mesmo.
              </p>

              {/* Ações — mantém AlertDialogCancel/Action (foco, ESC) */}
              <AlertDialogFooter className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-3">
                <AlertDialogCancel className="order-2 mt-0 flex-1 rounded-xl text-sm font-semibold sm:order-1">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  className="order-1 flex-1 rounded-xl text-sm font-semibold shadow-lg sm:order-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setConfirmAllOpen(false);
                    void runCharge();
                  }}
                >
                  Confirmar e pagar
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </form>
  );
}
